import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Input, Modal, Pagination, Segmented, Select, Upload, message } from "antd";
import { FilterOutlined, PlusOutlined, SearchOutlined, UploadOutlined } from "@ant-design/icons";

import { controller as rootController } from "../../controller";
// Every dashboard page wraps itself in this rather than the router doing it —
// it supplies the sidebar, top bar AND the ScrapeJobs WebSocket manager, which
// is what feeds the live-status pulse in the table.
import DashboardLayout from "../../layout/dashboardLayout/DashboardLayout";
// Shared with the FTC Data Entry chip: fetches the policy and reuses the same
// completion gate, so "what counts as complete" and how it is saved (diff-only,
// to avoid freezing PMS-owned fields) cannot drift between the two surfaces.
import FTCDataEntryModal from "../FTCTasks/FTCDataEntryModal";
import TreatmentPlanListRowInsuranceActions from "../TreatmentPlans/component/TreatmentPlanListRowInsuranceActions";

import RequestComposerDrawer from "./RequestComposerDrawer";
import { usePlanGuard } from "../Subscription/planModal";
import { outcomeChipStyle, outcomeLabel, readyFiltersFor } from "./outcomes";
import {
  REQUEST_TYPES, REQUEST_TYPE_ORDER, needsSubmissions, requestTypeMeta, tabForRequestType,
} from "./requestTypes";
import RequestsTable from "./RequestsTable";
import InfoAskReplyModal from "./InfoAskReplyModal";
import RequestResultsModal from "./RequestResultsModal";
import {
  RESUBMIT_ACCEPT, RESUBMIT_MAX_BYTES, askedFor, itemTitle, policyAskOf, waitingItems,
} from "./rowContext";
import { controller } from "./controller";
import "./insuranceRequests.css";

// Three tabs. What came back is on Ready — sorted under the filter dropdown in
// the toolbar, with anything that needs the office among them — what hasn't yet is In
// progress, and what the office has put away is Archived. A withdrawn request
// has no tab: nobody comes back to browse those (the backend still records and
// filters `cancelled`; we simply never ask for that list).
const TABS = [
  { key: "ready", label: "Ready" },
  { key: "in_progress", label: "In progress" },
  // No count: Archived is a store, not a to-do — its size only ever grows.
  { key: "archived", label: "Archived", hideCount: true },
];

const PAGE_SIZE = 25;

// The type filter: everything, or one request type. "" is "all" — the API
// ignores an absent request_type.
const TYPE_OPTIONS = [{ label: "All", value: "" }].concat(
  REQUEST_TYPE_ORDER.map(function (key) {
    return { label: REQUEST_TYPES[key].filterLabel, value: key };
  }),
);

const EMPTY = {
  ready: {
    title: "Nothing ready yet",
    body: "What comes back lands here, newest first — the carrier's answers, when we'll check again, and anything our team needs from you.",
  },
  in_progress: {
    title: "No requests in progress",
    body: "Anything you ask for shows up here until something comes back.",
  },
  archived: {
    title: "Nothing archived",
    body: "Archive a Ready request once you've dealt with it. It stays here, and a completed one can be moved back to Ready.",
  },
};

// A Ready filter with nothing under it.
const FILTER_EMPTY = {
  missing_info: "When our team needs something from you — details a carrier can't match, or a document only the patient can get — the request waits here until you resubmit it.",
  needs_resubmission: "When a carrier needs an estimate or claim sent again, the request waits here until you resubmit it — with the new file, if there is one.",
  breakdown_unavailable: "When our team is unable to obtain a breakdown from the carrier, the request closes here with the reason. Send a new request if you'd like us to try again.",
  correspondence_sent: "When a carrier sends its answer to the patient or member instead of you, the request waits here until you get it from them and resubmit — with the letter, if you have it.",
  covered: "Estimates the carrier will cover land here.",
  partially_covered: "Estimates the carrier will cover in part land here.",
  not_covered: "Estimates the carrier won't cover land here.",
  paid: "Claims the carrier has paid land here.",
  payment_processing: "When a carrier has approved a claim and payment is on its way, it waits here — our team checks again on the date shown.",
  follow_up_again: "When a carrier hasn't answered yet, the request waits here — our team checks again on the date shown and it moves back to the top.",
  contact_patient: "Set a Ready request's status to “Contact patient” and it waits here until you change it.",
  appointment_booked: "Set a Ready request's status to “Appointment booked” once the patient is booked.",
};

/**
 * Insurance Verification — the office admin's surface for asking about coverage.
 * (Directory, components, CSS prefix and API path all still say
 * "insurance-requests"; that is the internal name, this is the displayed one.)
 *
 * The Patients board and its modal are built for FTC
 * staff: patient-centric, dense, and full of decisions an office admin has no
 * basis to make (agent vs manual, which of three policies, breakdown vs
 * eligibility). This page inverts that. It is REQUEST-centric — one row per
 * thing you asked for — and it never mentions how the work gets done.
 */
// One Ready filter as the dropdown shows it: the outcome's dot, its name and
// how many requests are under it ("" = All).
function renderFilterOption(key, outcomeCounts) {
  const n = (key ? outcomeCounts[key] : outcomeCounts.all) || 0;
  const tint = key ? outcomeChipStyle(key) : null;
  return (
    <span className={"ir-outcome-option" + (n ? "" : " is-empty")}>
      {tint ? <span className="ir-filter-dot" style={{ background: tint.color }} /> : null}
      <span className="ir-outcome-option-label">{key ? outcomeLabel(key) : "All"}</span>
      <span className="ir-filter-count">{n}</span>
    </span>
  );
}

export default function InsuranceRequestsPage() {
  const [status, setStatus] = useState("ready");
  // The Ready filter ("" = All). One at a time; every Ready request is under
  // exactly one (the server decides which — views_insurance_requests.READY_KEYS).
  const [outcome, setOutcome] = useState("");
  const [requestType, setRequestType] = useState("");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [outcomeCounts, setOutcomeCounts] = useState({});
  // Setting a status and archiving are the practice's own actions — never FTC
  // personnel's. The server says which this user is (and enforces it).
  const [canManage, setCanManage] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  // Starting paid work needs a plan (or trial requests left); the list stays viewable.
  const guardPlan = usePlanGuard();
  const [fixing, setFixing] = useState(null);   // { patientId, policyId }
  // A request waiting on the office being sent back to our team, with the
  // office's reply and, per item handed back, an optional new file.
  const [resubmitting, setResubmitting] = useState(null);   // row
  const [resubmitNote, setResubmitNote] = useState("");
  const [resubmitFiles, setResubmitFiles] = useState({});   // { [item id]: antd fileList }
  const [resubmitBusy, setResubmitBusy] = useState(false);
  // A breakdown request whose Details Needed checklist the office is answering.
  const [replying, setReplying] = useState(null);   // row
  const [replyBusy, setReplyBusy] = useState(false);
  const [replyErrors, setReplyErrors] = useState({});
  const [viewing, setViewing] = useState(null); // { patient, policyId, tab, nonce }
  // "View Results" on an estimate or claim waiting on the office.
  const [resultsRow, setResultsRow] = useState(null);
  const [resultsBusy, setResultsBusy] = useState(false);
  // Platform super-admins get the per-row Delete. Resolved from the same
  // checkPracticeAdmin call every other admin surface uses, so "who is a super
  // admin" has one answer in the app; the server gates the endpoint regardless.
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const reqSeq = useRef(0);

  const load = useCallback(
    async function loadInner(opts) {
      const o = opts || {};
      const seq = ++reqSeq.current;
      const tab = o.status !== undefined ? o.status : status;
      setLoading(true);
      try {
        const res = await controller.getRequests({
          status: tab,
          outcome: tab === "ready" ? (o.outcome !== undefined ? o.outcome : outcome) : "",
          requestType: o.requestType !== undefined ? o.requestType : requestType,
          q: o.q !== undefined ? o.q : search,
          page: o.page !== undefined ? o.page : page,
          pageSize: PAGE_SIZE,
        });
        if (seq !== reqSeq.current) return;
        const body = (res && res.json) || {};
        if (res.status >= 400) {
          message.error(body.detail || "Couldn't load your requests.");
          setRows([]);
          setTotal(0);
          return;
        }
        setRows(body.results || []);
        setCounts(body.counts || {});
        setOutcomeCounts(body.outcome_counts || {});
        setCanManage(!!body.can_manage);
        setTotal(body.count || 0);
      } catch (e) {
        if (seq === reqSeq.current) {
          console.error(e);
          message.error("Couldn't load your requests.");
        }
      } finally {
        if (seq === reqSeq.current) setLoading(false);
      }
    },
    [status, outcome, requestType, search, page],
  );

  useEffect(
    function () {
      load();
    },
    [status, outcome, requestType, page], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(function () {
    let cancelled = false;
    rootController.checkPracticeAdmin().then(function (res) {
      if (cancelled) return;
      setIsSuperAdmin(Boolean(res && res.data && res.data.result === "super_admin"));
    }).catch(function () { /* not a super admin, or the check failed — no Delete. */ });
    return function () { cancelled = true; };
  }, []);

  // Debounce the patient filter so typing doesn't fire a request per keystroke.
  // Skipped on mount — the effect above already did the first load, and running
  // both would double every page open.
  const searchMounted = useRef(false);
  useEffect(
    function () {
      if (!searchMounted.current) {
        searchMounted.current = true;
        return undefined;
      }
      const t = setTimeout(function () {
        setPage(1);
        load({ q: search, page: 1 });
      }, 300);
      return function () { clearTimeout(t); };
    },
    [search], // eslint-disable-line react-hooks/exhaustive-deps
  );

  function handleTab(key) {
    setStatus(key);
    setOutcome("");
    setPage(1);
  }

  function handleOutcome(key) {
    setOutcome(key || "");
    setPage(1);
  }

  function handleType(value) {
    setRequestType(value);
    // A filter the new type doesn't have (Paid, on Estimates) would show an
    // empty list with nothing selected to explain it.
    if (outcome && readyFiltersFor(value).indexOf(outcome) === -1) setOutcome("");
    setPage(1);
  }

  function handleView(row) {
    setViewing({
      patient: {
        id: row.patient.id,
        first_name: row.patient.first_name,
        last_name: row.patient.last_name,
      },
      policyId: row.policy && row.policy.id,
      tab: tabForRequestType(row.request_type),
      nonce: Date.now(),
    });
  }

  function failed(res, fallback) {
    message.error(((res && res.json) || {}).detail || fallback);
  }

  function handleCancel(row) {
    Modal.confirm({
      title: "Withdraw this request?",
      content: "We'll stop working on it. You can always ask again later.",
      okText: "Withdraw",
      okButtonProps: { danger: true },
      cancelText: "Keep it",
      onOk: async function () {
        const res = await controller.cancelRequest(row.id);
        if (res && res.status < 300) {
          message.success("Request withdrawn.");
          load();
        } else {
          failed(res, "Couldn't withdraw the request.");
        }
      },
    });
  }

  // Archiving a request that is still open withdraws it (the server does both
  // in one go), so the confirm says so — it is the one archive that loses
  // something.
  function handleArchive(row) {
    const open = !row.completed_at;
    Modal.confirm({
      title: "Archive this request?",
      content: open
        ? "It isn't finished, so archiving also withdraws it — our team stops working on it. Whatever already came back stays on the patient's record."
        : "It moves to Archived. You can move it back to Ready at any time.",
      okText: "Archive",
      okButtonProps: open ? { danger: true } : undefined,
      cancelText: "Keep it",
      onOk: async function () {
        const res = await controller.archiveRequest(row.id);
        if (res && res.status < 300) {
          message.success(open ? "Archived and withdrawn." : "Archived.");
          load();
        } else {
          failed(res, "Couldn't archive the request.");
        }
      },
    });
  }

  async function handleRestore(row) {
    const res = await controller.restoreRequest(row.id);
    if (res && res.status < 300) {
      message.success("Moved back to Ready.");
      load();
    } else {
      failed(res, "Couldn't move the request back.");
    }
  }

  async function handleSetStatus(row, value) {
    const res = await controller.setOfficeStatus(row.id, value);
    if (res && res.status < 300) {
      message.success(value ? "Moved to “" + outcomeLabel(value) + "”." : "Status cleared.");
      load();
    } else {
      failed(res, "Couldn't set the status.");
    }
  }

  // Super-admin only. The copy is deliberately specific about what SURVIVES:
  // the row goes, but the breakdown or estimate it produced does not, and neither
  // does the billing record. Someone reaching for this needs to know that before
  // they click, not after.
  function handleDelete(row) {
    Modal.confirm({
      title: "Delete this request?",
      content: (
        <div>
          <p style={{ marginBottom: 8 }}>
            This permanently removes the request from the platform. It can't be undone.
          </p>
          <p style={{ marginBottom: 0, color: "#5c5f6e" }}>
            {requestTypeMeta(row.request_type).deleteNote}
          </p>
        </div>
      ),
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Keep it",
      onOk: async function () {
        const res = await controller.deleteRequest(row.id);
        if (res && res.status < 300) {
          message.success("Request deleted.");
          load();
        } else {
          failed(res, "Couldn't delete the request.");
        }
      },
    });
  }

  // The office filled in what we asked for, so the policy is complete again —
  // lift its Details Needed by Smilepass flag, and the request goes back to our
  // team. The note is not sent: it is our team's ask, and stays with the policy.
  async function handleFixed() {
    const target = fixing;
    setFixing(null);
    if (!target) return;
    try {
      await rootController.updatePolicyAttentionNote(target.policyId, undefined, { needsAttention: false });
    } catch (e) {
      console.error(e);
    }
    load();
  }

  function openResubmit(row) {
    setResubmitNote("");
    setResubmitFiles({});
    setResubmitting(row);
  }

  // The office's answers to our team's checklist go through the same Resubmit
  // call, field by field; the server names any box that needs fixing.
  async function handleReply(payload) {
    const row = replying;
    if (!row) return;
    setReplyBusy(true);
    try {
      const res = await controller.resubmitRequest(row.id, payload.note, {}, { answers: payload.answers, skipped: payload.skipped });
      if (!(res && res.status >= 200 && res.status < 300)) {
        if (res && res.json && res.json.fields) {
          setReplyErrors(res.json.fields);
          message.error(res.json.detail || "Some answers need fixing.");
        } else {
          failed(res, "Couldn't send the details.");
        }
        return;
      }
      message.success("Sent to our team.");
      setReplying(null);
      setReplyErrors({});
      load();
    } finally {
      setReplyBusy(false);
    }
  }

  // "Update Smilepass on progress" from the results modal: the same reply as
  // a resubmission — the note (and a file per item) goes on every item waiting
  // on the office, and the request goes back to our team to follow up.
  async function handleResultsUpdate(payload) {
    const row = resultsRow;
    if (!row) return;
    setResultsBusy(true);
    try {
      const res = await controller.resubmitRequest(row.id, payload.note, payload.files);
      if (!(res && res.status >= 200 && res.status < 300)) {
        failed(res, "Couldn't send your update.");
        return;
      }
      message.success(row.initiated_by_ftc
        ? "Thanks — it's back with our team. The result will be in your daily report."
        : "Thanks — it's back with our team; we'll follow it up.");
      setResultsRow(null);
      load();
    } finally {
      setResultsBusy(false);
    }
  }

  // Resubmitting answers everything on the request that is waiting on the
  // office in one go: each item our team handed back gets the reply (and its
  // new file) on its own timeline, and a Details Needed by Smilepass flag on the
  // policy lifts with it — so the request goes back to our team and reappears
  // in the FTC queue.
  async function handleResubmit() {
    const row = resubmitting;
    if (!row) return;
    const files = {};
    Object.keys(resubmitFiles).forEach(function (id) {
      const list = resubmitFiles[id] || [];
      if (list.length && list[0].originFileObj) files[id] = list[0].originFileObj;
    });
    setResubmitBusy(true);
    try {
      const res = await controller.resubmitRequest(row.id, resubmitNote.trim(), files);
      if (!(res && res.status >= 200 && res.status < 300)) {
        failed(res, "Couldn't resubmit the request.");
        return;
      }
      // One our team started goes back off this page once answered; its result
      // comes in the office's daily report.
      message.success(row.initiated_by_ftc
        ? "Resubmitted — it's back with our team. The result will be in your daily report."
        : "Resubmitted — it's back with our team.");
      setResubmitting(null);
      setResubmitNote("");
      setResubmitFiles({});
      load();
    } finally {
      setResubmitBusy(false);
    }
  }

  const filters = readyFiltersFor(requestType);
  let empty = EMPTY[status] || EMPTY.ready;
  if (status === "ready" && outcome) {
    empty = { title: "Nothing under “" + outcomeLabel(outcome) + "”", body: FILTER_EMPTY[outcome] || empty.body };
  }

  const resubmitMeta = resubmitting ? requestTypeMeta(resubmitting.request_type) : null;
  const handedBack = waitingItems(resubmitting);
  // The policy's own flag, when it is what the office is answering: the row's
  // note is then the policy's ("the member id doesn't match").
  const policyAsk = policyAskOf(resubmitting);

  return (
    <DashboardLayout>
    <div className="ir-page">
      <div className="ir-header">
        <div>
          <h1 className="ir-title">Insurance Verification</h1>
          {/* Which kind of request the page lists, right under the title: it
              decides everything below it (tabs, filters, rows). */}
          <Segmented
            className="ir-type-filter"
            value={requestType}
            onChange={handleType}
            options={TYPE_OPTIONS}
          />
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={function () { guardPlan(function () { setComposerOpen(true); }); }}
        >
          New request
        </Button>
      </div>

      <div className="ir-toolbar">
        <div className="ir-tabs">
          {TABS.map(function (tab) {
            const n = tab.hideCount ? 0 : counts[tab.key];
            return (
              <button
                key={tab.key}
                type="button"
                className={"ir-tab" + (status === tab.key ? " is-active" : "")}
                onClick={function () { handleTab(tab.key); }}
              >
                {tab.label}
                {n ? <span className="ir-tab-count">{n}</span> : null}
              </button>
            );
          })}
        </div>
        <div className="ir-toolbar-right">
          {/* The Ready filters: every one the request type has, empty ones
              dimmed, each with its count, so the list doesn't jump about as
              counts change. */}
          {status === "ready" ? (
            <Select
              className="ir-outcome-select"
              popupClassName="ir-outcome-select-popup"
              aria-label="Filter what's ready"
              value={outcome}
              onChange={handleOutcome}
              popupMatchSelectWidth={false}
              listHeight={420}
              prefix={<FilterOutlined style={{ color: "#848696" }} />}
              options={[""].concat(filters).map(function (key) {
                return { value: key, label: key ? outcomeLabel(key) : "All" };
              })}
              labelRender={function (props) { return renderFilterOption(props.value, outcomeCounts); }}
              optionRender={function (option) { return renderFilterOption(option.value, outcomeCounts); }}
            />
          ) : null}
          <Input
            className="ir-search"
            allowClear
            prefix={<SearchOutlined style={{ color: "#848696" }} />}
            placeholder="Filter by patient"
            value={search}
            onChange={function (e) { setSearch(e.target.value); }}
          />
        </div>
      </div>


      {!loading && rows.length === 0 ? (
        <div className="ir-empty">
          <p className="ir-empty-title">{empty.title}</p>
          <p className="ir-empty-body">{empty.body}</p>
          {status !== "archived" ? (
            <Button type="primary" onClick={function () { guardPlan(function () { setComposerOpen(true); }); }}>
              New request
            </Button>
          ) : null}
        </div>
      ) : (
        <>
          <div className="ir-table-card">
            <RequestsTable
              rows={rows}
              loading={loading}
              tab={status}
              canManage={canManage}
              onView={handleView}
              onFix={function (row) {
                setFixing({ patientId: row.patient.id, policyId: row.policy.id });
              }}
              onReply={function (row) { guardPlan(function () { setReplyErrors({}); setReplying(row); }); }}
              onResubmit={function (row) {
                guardPlan(function () {
                  // An estimate or claim opens what our team found ("View
                  // Results"); a breakdown on an old flag keeps the Resubmit dialog.
                  if (needsSubmissions(row.request_type)) setResultsRow(row);
                  else openResubmit(row);
                });
              }}
              onCancel={handleCancel}
              onArchive={handleArchive}
              onRestore={handleRestore}
              onSetStatus={handleSetStatus}
              canDelete={isSuperAdmin}
              onDelete={handleDelete}
            />
          </div>
          {total > PAGE_SIZE ? (
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
              <Pagination
                current={page}
                pageSize={PAGE_SIZE}
                total={total}
                showSizeChanger={false}
                onChange={setPage}
              />
            </div>
          ) : null}
        </>
      )}

      <RequestComposerDrawer
        open={composerOpen}
        onClose={function () { setComposerOpen(false); }}
        onSubmitted={function () {
          // Land the user where their new request actually is.
          setStatus("in_progress");
          setOutcome("");
          setPage(1);
        }}
      />

      <Modal
        open={!!resubmitting}
        title="Send this back to our team?"
        okText="Resubmit"
        confirmLoading={resubmitBusy}
        onOk={handleResubmit}
        onCancel={function () { setResubmitting(null); }}
        destroyOnClose
      >
        <p style={{ marginBottom: 12 }}>
          Our team picks it up again, with your reply beside what they asked for. Say what changed, if anything.
        </p>
        {policyAsk ? (
          <div className="ir-resubmit-item">
            <div className="ir-resubmit-item-head">
              <span className="ir-resubmit-item-title">What our team asked</span>
            </div>
            <div className="ir-resubmit-item-note">{policyAsk}</div>
          </div>
        ) : null}
        {handedBack.map(function (item) {
          const all = resubmitting.requested_estimates || [];
          const ask = askedFor(item);
          const tint = outcomeChipStyle(item.outcome);
          return (
            <div key={item.id} className="ir-resubmit-item">
              <div className="ir-resubmit-item-head">
                <span className="ir-resubmit-item-title">
                  {itemTitle(item, resubmitMeta, all.indexOf(item), all.length)}
                </span>
                <span className="ir-resubmit-item-outcome" style={tint}>{outcomeLabel(item.outcome)}</span>
              </div>
              {ask ? <div className="ir-resubmit-item-note">{ask}</div> : null}
              <Upload
                accept={RESUBMIT_ACCEPT}
                maxCount={1}
                fileList={resubmitFiles[item.id] || []}
                beforeUpload={function (file) {
                  if (file.size > RESUBMIT_MAX_BYTES) {
                    message.error("That file is larger than 20MB. Try a smaller scan or photo.");
                    return Upload.LIST_IGNORE;
                  }
                  return false;   // sent with the resubmission, not on its own
                }}
                onChange={function (info) {
                  const list = info.fileList.slice(-1);
                  setResubmitFiles(function (prev) {
                    const next = Object.assign({}, prev);
                    next[item.id] = list;
                    return next;
                  });
                }}
              >
                <Button size="small" icon={<UploadOutlined />}>
                  {(resubmitFiles[item.id] || []).length ? "Replace the file" : "Attach a new file (optional)"}
                </Button>
              </Upload>
            </div>
          );
        })}
        <Input.TextArea
          rows={3}
          value={resubmitNote}
          onChange={function (e) { setResubmitNote(e.target.value); }}
          placeholder="Optional — e.g. the corrected claim is attached"
          maxLength={2000}
        />
      </Modal>

      {fixing ? (
        <FTCDataEntryModal
          patientId={fixing.patientId}
          policyId={fixing.policyId}
          onClose={function () { setFixing(null); }}
          onSaved={handleFixed}
        />
      ) : null}

      {resultsRow ? (
        <RequestResultsModal
          key={resultsRow.id}
          row={resultsRow}
          busy={resultsBusy}
          onCancel={function () { setResultsRow(null); }}
          onSubmit={handleResultsUpdate}
          onViewHistory={function () {
            const row = resultsRow;
            setResultsRow(null);
            handleView(row);
          }}
        />
      ) : null}

      {replying ? (
        <InfoAskReplyModal
          key={replying.id}
          row={replying}
          busy={replyBusy}
          fieldErrors={replyErrors}
          onCancel={function () { setReplying(null); setReplyErrors({}); }}
          onSubmit={handleReply}
        />
      ) : null}

      {/* Headless reuse of the row's insurance modal for "View coverage" — the
          full breakdown/estimate viewer already exists and is the right place
          for someone who wants the detail. Keyed so a repeat click reopens. */}
      {viewing ? (
        <TreatmentPlanListRowInsuranceActions
          key={viewing.nonce}
          patient={viewing.patient}
          hideTriggers
          // Reader mode: the office admin asked for this verification, they
          // didn't perform it — so the carrier contact details, the office's
          // claim-submission identity, the portal login and the whole
          // request/add/upload rail are hidden. Every other surface that opens
          // this modal keeps them.
          hideVerificationTools
          autoOpenInsuranceTab={viewing.tab}
          defaultSelectedPolicyId={viewing.policyId}
          onInsuranceModalClosed={function () { setViewing(null); }}
        />
      ) : null}
    </div>
    </DashboardLayout>
  );
}
